import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/context/AuthContext';
import { showSnackbar } from '@/shared/hooks/useSnackbar';

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

/**
 * Logs the user out after `user.idleLogoutMinutes` of inactivity.
 * `0` or missing disables idle logout (e.g. super_admin).
 */
const IdleLogoutWatcher: React.FC = () => {
  const { t } = useTranslation('auth');
  const { user, isAuthenticated, logout } = useAuth();
  const lastActivityRef = useRef(Date.now());
  const loggingOutRef = useRef(false);

  const idleMinutes = user?.idleLogoutMinutes ?? 0;

  useEffect(() => {
    lastActivityRef.current = Date.now();
    loggingOutRef.current = false;
  }, [idleMinutes, isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !idleMinutes || idleMinutes <= 0) {
      return;
    }

    const idleMs = idleMinutes * 60 * 1000;

    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, markActivity, { passive: true });
    }

    const intervalId = window.setInterval(() => {
      if (loggingOutRef.current) return;
      if (Date.now() - lastActivityRef.current < idleMs) return;
      loggingOutRef.current = true;
      showSnackbar(t('idleLoggedOut'), 'warning');
      logout();
    }, 15_000);

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, markActivity);
      }
      window.clearInterval(intervalId);
    };
  }, [idleMinutes, isAuthenticated, logout, t]);

  return null;
};

export default IdleLogoutWatcher;
